import Konva from 'konva'
import type { AnnotationShape, BoxShape, LineShape, TextShape } from '@/utils/imageEdit'

export const ANNOTATION_NAME = 'annotation'
export const TEXT_FONT_FAMILY = 'Roboto, Arial, sans-serif'

export const arrowHeadSize = (strokeWidth: number) => Math.max(10, strokeWidth * 4)

export function createAnnotationNode(shape: AnnotationShape): Konva.Shape {
  const common = { id: shape.id, name: ANNOTATION_NAME, strokeScaleEnabled: false }

  switch (shape.type) {
    case 'rectangle':
      return new Konva.Rect({
        ...common,
        x: shape.x,
        y: shape.y,
        width: shape.width,
        height: shape.height,
        offsetX: shape.width / 2,
        offsetY: shape.height / 2,
        rotation: shape.rotation,
        stroke: shape.color,
        strokeWidth: shape.strokeWidth,
        fill: 'rgba(0,0,0,0)',
      })
    case 'ellipse':
      return new Konva.Ellipse({
        ...common,
        x: shape.x,
        y: shape.y,
        radiusX: shape.width / 2,
        radiusY: shape.height / 2,
        rotation: shape.rotation,
        stroke: shape.color,
        strokeWidth: shape.strokeWidth,
        fill: 'rgba(0,0,0,0)',
      })
    case 'line':
    case 'arrow': {
      const head = shape.type === 'arrow' ? arrowHeadSize(shape.strokeWidth) : 0
      return new Konva.Arrow({
        ...common,
        points: [...shape.points],
        stroke: shape.color,
        fill: shape.color,
        strokeWidth: shape.strokeWidth,
        lineCap: 'round',
        pointerLength: head,
        pointerWidth: head,
        hitStrokeWidth: Math.max(shape.strokeWidth, 20),
      })
    }
    case 'text':
      return new Konva.Text({
        ...common,
        x: shape.x,
        y: shape.y,
        text: shape.text,
        fontSize: shape.fontSize,
        fontFamily: TEXT_FONT_FAMILY,
        fill: shape.color,
        rotation: shape.rotation,
        lineHeight: 1.2,
      })
  }
}

export function readAnnotationNode(node: Konva.Node, shape: AnnotationShape): AnnotationShape {
  const scaleX = node.scaleX()
  const scaleY = node.scaleY()

  switch (shape.type) {
    case 'rectangle':
    case 'ellipse': {
      const box = node as Konva.Rect | Konva.Ellipse
      const baseWidth = box instanceof Konva.Ellipse ? box.radiusX() * 2 : box.width()
      const baseHeight = box instanceof Konva.Ellipse ? box.radiusY() * 2 : box.height()
      return {
        ...shape,
        x: node.x(),
        y: node.y(),
        width: Math.abs(baseWidth * scaleX),
        height: Math.abs(baseHeight * scaleY),
        rotation: node.rotation(),
      } satisfies BoxShape
    }
    case 'line':
    case 'arrow': {
      const [x1, y1, x2, y2] = shape.points
      return {
        ...shape,
        points: [x1 + node.x(), y1 + node.y(), x2 + node.x(), y2 + node.y()],
      } satisfies LineShape
    }
    case 'text':
      return {
        ...shape,
        x: node.x(),
        y: node.y(),
        fontSize: shape.fontSize * Math.abs(scaleY),
        rotation: node.rotation(),
      } satisfies TextShape
  }
}
